import {useState, useEffect, useCallback, useRef} from 'react';
import {Client, StompSubscription} from '@stomp/stompjs';
import {GuestWebSocketMessage, GuestConnectorStatusUpdate} from 'src/types/guest';
import {ConnectorStatusEnum} from 'src/enums/chargers';

interface UseGuestSessionWebSocketReturn {
    wsData: GuestWebSocketMessage | null;
    connectorStatus: ConnectorStatusEnum | null;
    isConnected: boolean;
    reconnect: () => void;
}

interface UseGuestSessionWebSocketParams {
    connectorUuid: string | null;
    sessionUuid: string | null;
}

export const useGuestSessionWebSocket = ({
    connectorUuid,
    sessionUuid,
}: UseGuestSessionWebSocketParams): UseGuestSessionWebSocketReturn => {
    const [wsData, setWsData] = useState<GuestWebSocketMessage | null>(null);
    const [connectorStatus, setConnectorStatus] = useState<ConnectorStatusEnum | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const clientRef = useRef<Client | null>(null);
    const connectorSubscriptionRef = useRef<StompSubscription | null>(null);
    const sessionSubscriptionRef = useRef<StompSubscription | null>(null);

    const connect = useCallback(() => {
        // Need at least connectorUuid to connect
        if (!connectorUuid) return;

        // Clean up the existing connection
        if (clientRef.current?.connected) {
            connectorSubscriptionRef.current?.unsubscribe();
            sessionSubscriptionRef.current?.unsubscribe();
            clientRef.current.deactivate();
        }

        const client = new Client({
            brokerURL: `${process.env.REACT_APP_WS_URL}`,
            debug: () => {
                // WebSocket debug logs disabled
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
        });

        client.onConnect = () => {
            setIsConnected(true);

            // Subscribe to a connector status topic (always)
            connectorSubscriptionRef.current = client.subscribe(
                `/topic/guest-connector/${connectorUuid}`,
                (message) => {
                    if (message.body) {
                        try {
                            const data = JSON.parse(message.body) as GuestConnectorStatusUpdate;
                            setConnectorStatus(data.status);
                        } catch (error) {
                            console.error('Failed to parse connector WebSocket message:', error);
                        }
                    }
                }
            );

            // Subscribe to a session topic (only if sessionUuid is provided)
            if (sessionUuid) {
                sessionSubscriptionRef.current = client.subscribe(
                    `/topic/guest-session/${sessionUuid}`,
                    (message) => {
                        if (message.body) {
                            try {
                                const data = JSON.parse(message.body) as GuestWebSocketMessage;
                                setWsData(data);
                            } catch (error) {
                                console.error('Failed to parse session WebSocket message:', error);
                            }
                        }
                    }
                );
            }
        };

        client.onDisconnect = () => {
            setIsConnected(false);
        };

        client.onStompError = (frame) => {
            console.error('WebSocket STOMP error:', frame.headers.message);
            setIsConnected(false);
        };

        client.onWebSocketClose = () => {
            setIsConnected(false);
        };

        clientRef.current = client;
        client.activate();
    }, [connectorUuid, sessionUuid]);

    // Subscribe to a session topic when sessionUuid becomes available
    useEffect(() => {
        if (sessionUuid && clientRef.current?.connected && !sessionSubscriptionRef.current) {
            sessionSubscriptionRef.current = clientRef.current.subscribe(
                `/topic/guest-session/${sessionUuid}`,
                (message) => {
                    if (message.body) {
                        try {
                            const data = JSON.parse(message.body) as GuestWebSocketMessage;
                            setWsData(data);
                        } catch (error) {
                            console.error('Failed to parse session WebSocket message:', error);
                        }
                    }
                }
            );
        }
    }, [sessionUuid]);

    const reconnect = useCallback(() => {
        connect();
    }, [connect]);

    // Connect when connectorUuid is available
    useEffect(() => {
        if (connectorUuid) {
            connect();
        }

        return () => {
            // Cleanup on unmounting
            if (connectorSubscriptionRef.current) {
                connectorSubscriptionRef.current.unsubscribe();
                connectorSubscriptionRef.current = null;
            }
            if (sessionSubscriptionRef.current) {
                sessionSubscriptionRef.current.unsubscribe();
                sessionSubscriptionRef.current = null;
            }
            if (clientRef.current?.connected) {
                clientRef.current.deactivate();
            }
        };
    }, [connectorUuid, connect]);

    return {
        wsData,
        connectorStatus,
        isConnected,
        reconnect,
    };
};

export default useGuestSessionWebSocket;
